import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Logo from '../components/Logo'

export default function OnboardingDestinationsPage() {
  const navigate = useNavigate()
  const [open, setOpen] = useState<string | null>(null)
  const [connected, setConnected] = useState<Record<string, boolean>>({})

  const toggle = (id: string) => {
    if (connected[id]) return
    setOpen(prev => prev === id ? null : id)
  }

  const connect = (e: React.MouseEvent, id: string) => {
    e.stopPropagation()
    setConnected(c => ({ ...c, [id]: true }))
    setOpen(null)
  }

  return (
    <div className="min-h-screen bg-[#f5f5f4] dark:bg-[#111] flex items-center justify-center p-5">
      <div className="bg-white dark:bg-[#1e1e1e] border border-[#e5e5e3] dark:border-[#2e2e2e] rounded-xl p-9 w-full max-w-[580px]">
        <div className="mb-7"><Logo size="lg" /></div>
        <StepIndicator current={3} />

        <h1 className="text-[20px] font-medium tracking-[-0.3px] dark:text-[#f0f0ef] mb-1.5">Where should skills be published?</h1>
        <p className="text-[14px] text-[#888] dark:text-[#666] mb-7 leading-relaxed">
          Connect the AI platforms you want to publish skills to. Skills will be formatted correctly for each platform automatically.
        </p>

        {/* Open WebUI */}
        <DestCard
          id="wu" icon="⬡" iconBg="bg-[#eff4ff]"
          name="Open WebUI" desc="Publish to your self-hosted Open WebUI instance"
          open={open === 'wu'} connected={connected['wu']}
          onToggle={() => toggle('wu')}
        >
          <input className="c-input" placeholder="Instance URL (e.g. https://ai.slimdoli.com)" />
          <input className="c-input" type="password" placeholder="API key" />
          <p className="text-[11px] text-[#aaa] dark:text-[#555] leading-relaxed mb-2">
            Find your API key in Open WebUI → Profile → Account → API Keys
          </p>
          <button onClick={e => connect(e, 'wu')} className="c-save">Connect</button>
        </DestCard>

        {/* Claude API */}
        <DestCard
          id="cl" icon="◈" iconBg="bg-[#fef3e2]"
          name="Claude API" desc="Validate skills and inject via system prompt"
          open={open === 'cl'} connected={connected['cl']}
          onToggle={() => toggle('cl')}
        >
          <input className="c-input" type="password" placeholder="Anthropic API key (sk-ant-...)" />
          <p className="text-[11px] text-[#aaa] dark:text-[#555] leading-relaxed mb-2">
            Find your key at console.anthropic.com → API Keys
          </p>
          <button onClick={e => connect(e, 'cl')} className="c-save">Connect</button>
        </DestCard>

        <button
          onClick={() => navigate('/onboarding/import')}
          className="w-full mt-3 py-2.5 bg-[#1a1a1a] dark:bg-[#f0f0ef] text-white dark:text-[#1a1a1a] rounded-[7px] text-[14px] font-medium hover:bg-[#333] dark:hover:bg-[#ddd] cursor-pointer"
        >
          Continue →
        </button>
        <button
          onClick={() => navigate('/onboarding/import')}
          className="w-full mt-2 py-2.5 bg-transparent border border-[#e5e5e3] dark:border-[#2e2e2e] rounded-[7px] text-[14px] font-medium hover:bg-[#f5f5f4] dark:hover:bg-[#252525] cursor-pointer text-[#1a1a1a] dark:text-[#f0f0ef]"
        >
          Skip for now
        </button>
      </div>
    </div>
  )
}

function DestCard({ id, icon, iconBg, name, desc, open, connected, onToggle, children }: {
  id: string; icon: string; iconBg: string; name: string; desc: string;
  open: boolean; connected: boolean; onToggle: () => void; children: React.ReactNode
}) {
  return (
    <div className={`border rounded-lg mb-2 overflow-hidden ${connected ? 'border-[#16a34a]' : 'border-[#e5e5e3] dark:border-[#2e2e2e]'}`}>
      <div onClick={onToggle} className="flex items-center gap-2.5 p-3.5 cursor-pointer hover:bg-[#fafaf9] dark:hover:bg-[#161616]">
        <div className={`w-[34px] h-[34px] ${iconBg} rounded-[7px] flex items-center justify-center text-[16px] flex-shrink-0`}>{icon}</div>
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-medium dark:text-[#f0f0ef]">{name}</div>
          <div className="text-[11px] text-[#888] dark:text-[#666] mt-0.5">{desc}</div>
        </div>
        {connected
          ? <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-[#e8f5e9] text-[#16a34a] flex-shrink-0">✓ Connected</span>
          : <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-[#f0f0ef] dark:bg-[#252525] text-[#888] dark:text-[#666] flex-shrink-0">Optional</span>
        }
      </div>
      {open && !connected && (
        <div className="px-3.5 pb-3.5 pt-3 border-t border-[#e5e5e3] dark:border-[#2e2e2e]">{children}</div>
      )}
    </div>
  )
}

// shared input styles
const destStyleEl = document.createElement('style')
destStyleEl.textContent = `.c-input { width: 100%; padding: 7px 10px; border: 1px solid #e5e5e3; border-radius: 6px; font-size: 13px; color: #1a1a1a; background: #fff; outline: none; font-family: inherit; margin-bottom: 6px; } .c-input:focus { border-color: #bbb; } .c-input::placeholder { color: #ccc; } .c-save { padding: 5px 14px; background: #1a1a1a; color: #fff; border: none; border-radius: 5px; font-size: 12px; font-weight: 500; cursor: pointer; } .c-save:hover { background: #333; } @media (prefers-color-scheme: dark) { .c-input { background: #222; color: #f0f0ef; border-color: #2e2e2e; } .c-input::placeholder { color: #555; } .c-input:focus { border-color: #555; } .c-save { background: #f0f0ef; color: #1a1a1a; } .c-save:hover { background: #ddd; } }`
document.head.appendChild(destStyleEl)

function StepIndicator({ current }: { current: number }) {
  const steps = ['Account', 'Sources', 'Destinations', 'Import']
  return (
    <div className="flex items-center justify-center mb-7">
      {steps.map((label, i) => {
        const n = i + 1
        const isDone = n < current
        const isCurrent = n === current
        return (
          <React.Fragment key={n}>
            {i > 0 && <div className="w-7 h-px bg-[#e5e5e3] dark:bg-[#2e2e2e] mx-1.5" />}
            <div className="flex items-center">
              <div className={`w-[26px] h-[26px] rounded-full flex items-center justify-center text-[11px] font-semibold flex-shrink-0 ${isDone || isCurrent ? 'bg-[#1a1a1a] dark:bg-[#f0f0ef] text-white dark:text-[#1a1a1a]' : 'bg-[#f0f0ef] dark:bg-[#252525] text-[#bbb] dark:text-[#444]'}`}>
                {isDone ? '✓' : n}
              </div>
              <span className={`text-[11px] ml-1.5 whitespace-nowrap ${isCurrent ? 'text-[#1a1a1a] dark:text-[#f0f0ef] font-medium' : 'text-[#bbb] dark:text-[#444]'}`}>{label}</span>
            </div>
          </React.Fragment>
        )
      })}
    </div>
  )
}
